import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { canReadPlatformAdmin, canWritePlatformAdmin, isPlatformSuperAdmin } from '../utils/platformAccess';
import { trackException } from '../services/appInsightsService';
import Button from './ui/Button';
import Input from './ui/Input';
import PlansManagement from './PlansManagement';
import TemplatesManagement from './TemplatesManagement';
import PlanEditor from './PlanEditor';
import TierDistribution from './TierDistribution';
import UnlimitedPlanAssignment from './UnlimitedPlanAssignment';

import RealtimeProviderPolicySettings from './RealtimeProviderPolicySettings';
import ClientSwitchesSettings from './ClientSwitchesSettings';
import NonprofitApplicationsQueue from './NonprofitApplicationsQueue';
import MeetingLimitsSection from './MeetingLimitsSection';
import UsageCatalogSection from './UsageCatalogSection';
import UsageLedgerSection from './UsageLedgerSection';
import PimTiersSection, { SUPPORTED_TIERS } from './PimTiersSection';
import ModelPolicySection from './ModelPolicySection';
import {
    Users, Building, ArrowLeft, BookOpen,
    CheckCircle, AlertCircle, Loader2, Clock, Infinity, Plus, RefreshCw,
    DollarSign, Pencil, Trash2, MessageSquare, Search, Copy, MoreVertical,
    UserPlus, Eye, EyeOff, KeyRound, Tag, BarChart3, Sliders, Coins, UserCog, ArrowRightLeft, HeartHandshake
} from 'lucide-react';
import './AdminSettings.css';

const SIGNUP_PLATFORMS = { zoom: 'Zoom', ios: 'iOS', web: 'Web', unknown: 'Unknown' };
const signupPlatform = (user) => Object.hasOwn(SIGNUP_PLATFORMS, user.SignupPlatform)
    ? user.SignupPlatform : 'unknown';

/**
 * Which of the two user lists to build — NOT which to show.
 *
 * The table and the card list were both rendered and one was hidden with `display: none`, so a
 * phone paid for the full desktop table it never displays. With every user row carrying selects
 * over every tenant and every plan, that hidden half is not free: it is half of an
 * N-users x M-tenants DOM, and on a memory-constrained browser it is what turns a large tenant
 * into a crashed tab. 860px is the same breakpoint the stylesheet switches on.
 */
function useUsersLayout() {
    const query = '(max-width: 860px)';
    const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false);
    const [isNarrow, setIsNarrow] = useState(read);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mql = window.matchMedia(query);
        const onChange = (event) => setIsNarrow(event.matches);
        setIsNarrow(mql.matches);

        // `MediaQueryList.addEventListener` only arrived in Safari 14. On the older iOS browsers
        // this whole change exists to keep alive, it is undefined — calling it throws during the
        // first render and takes the page down, which is the crash we are here to remove rather
        // than relocate. The deprecated pair is the one that has always been there.
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }
        if (typeof mql.addListener === 'function') {
            mql.addListener(onChange);
            return () => mql.removeListener(onChange);
        }
        // Neither hook exists: the first read above still gave the right list for this width.
        return undefined;
    }, []);

    return isNarrow ? 'cards' : 'table';
}

// Role definitions. `tenantLevel` mirrors CustomerConstants.GetRoleLevel and `platformLevel`
// mirrors GetPlatformRoleLevel — the two hierarchies UpdateUser judges a role grant by.
const TENANT_ROLES = [
    { value: 'Owner', label: 'Owner', tenantLevel: 4 },
    { value: 'Admin', label: 'Admin', tenantLevel: 3 },
    { value: 'Member', label: 'Member', tenantLevel: 2 },
    { value: 'Viewer', label: 'Viewer', tenantLevel: 1 },
];

const PLATFORM_ROLES = [
    { value: 'SuperAdmin', label: 'SuperAdmin', tenantLevel: 6, platformLevel: 5 },
    { value: 'PlatformContributor', label: 'Platform Contributor', tenantLevel: 5, platformLevel: 4 },
    { value: 'PlatformReadOnly', label: 'Platform ReadOnly', tenantLevel: 5, platformLevel: 3 },
    { value: 'Unlimited', label: 'Unlimited', tenantLevel: 5, platformLevel: 2 },
];

const ALL_ROLES = [...PLATFORM_ROLES, ...TENANT_ROLES];

// The roles UpdateUser will actually grant for this caller, judged from the JWT role claim
// (JwtGenerator puts the user's Role there). A platform role needs a platform level of at least
// 4 and strictly above the role's own; a tenant role needs a tenant level strictly above it.
// So no one is offered SuperAdmin: the server refuses it to every caller, a SuperAdmin included.
// MasterTenantBypassGrant adds one rule that depends on the target: below SuperAdmin, a master-
// tenant user cannot be given Owner, Admin or Unlimited, because those are billing bypass there.
// Before any hierarchy, UpdateUser refuses a caller's own role change (JWT `sub` = RowKey). Its
// cross-tenant refusal below platform level 4 never reaches a picker: only writers (level 4+)
// are shown one.
// #6912: the caller must also strictly outrank the target's current role — platform callers (4+) on
// the platform scale, where a tenant role ranks 0, everyone else on the tenant scale.
const getRoleChangeBlockedReason = (currentUser, target) => {
    if (!target) return '';
    if (currentUser?.RowKey && currentUser.RowKey === target.RowKey) {
        return 'You cannot change your own role.';
    }
    const caller = ALL_ROLES.find(r => r.value === currentUser?.Role);
    const current = ALL_ROLES.find(r => r.value === target.Role);
    const platformScale = (caller?.platformLevel || 0) >= 4;
    const callerRank = platformScale ? caller.platformLevel : (caller?.tenantLevel || 0);
    const targetRank = platformScale ? (current?.platformLevel || 0) : (current?.tenantLevel || 0);
    if (callerRank <= targetRank) {
        return 'You cannot change the role of a user ranked at or above your own.';
    }
    return '';
};

const getAssignableRoles = (currentUser, target) => {
    if (getRoleChangeBlockedReason(currentUser, target)) return [];
    const caller = ALL_ROLES.find(r => r.value === currentUser?.Role);
    const callerPlatformLevel = caller?.platformLevel || 0;
    const callerTenantLevel = caller?.tenantLevel || 0;
    const bypassRefused = callerPlatformLevel < 5 && target?.TenantId === MASTER_TENANT_ID;
    return ALL_ROLES.filter(role => (role.platformLevel
        ? callerPlatformLevel >= 4 && callerPlatformLevel > role.platformLevel
        : callerTenantLevel > role.tenantLevel)
        && !(bypassRefused && MASTER_TENANT_BYPASS_ROLES.includes(role.value)));
};

const roleLabel = (value) => ALL_ROLES.find(r => r.value === value)?.label || value;

// Only platform-level roles automatically receive Unlimited tier when assigned.
// Owner/Admin are normal tenant roles — they get billing bypass only in the C2HL master tenant,
// not via IsFreeTier. Auto-upgrading them caused ALL tenant Owners/Admins to show as Unlimited.
const ROLES_THAT_AUTO_UPGRADE = ['PlatformContributor', 'PlatformReadOnly', 'Unlimited'];

// The UpdateUser body for a role change — shared by the table's role popover and the card's sheet.
const roleUpdate = (role) => (ROLES_THAT_AUTO_UPGRADE.includes(role)
    ? { role, isFreeTier: false }
    : { role });

// Owner, Admin and Unlimited in the master tenant are billing bypass (MasterTenantBypassGrant.cs).
const MASTER_TENANT_ID = 'c2hl';
const MASTER_TENANT_BYPASS_ROLES = ['Owner', 'Admin', 'Unlimited'];

// Platform-only roles: the "Set Free Tier" button is hidden for these.
// Owner/Admin are intentionally excluded so C2HL admins can manually downgrade a user if needed.
const PRIVILEGED_ROLES = ['SuperAdmin', 'PlatformContributor', 'PlatformReadOnly', 'Unlimited'];

// Helper to check if data is from integration tests
const isIntegrationTestData = (item) => {
    return item?.Metadata?.source === 'integration-test';
};

// Format a plan object for display (e.g. { productType: "meeting", tier: "Pro" } → "Meeting Pro")
const formatPlanLabel = (plan) => {
    if (!plan) return '';
    if (plan.displayTier) return plan.displayTier;
    const product = (plan.productType || '').charAt(0).toUpperCase() + (plan.productType || '').slice(1);
    return [product, plan.tier].filter(Boolean).join(' ');
};

// Deduplicate catalog versions without collapsing Webinar attendee tiers that share a billing tier.
const deduplicatePlans = (plansList) => {
    const map = new Map();
    for (const p of plansList) {
        const key = `${p.productType}:${p.displayTier || p.tier}`;
        const existing = map.get(key);
        if (!existing || (p.id || '') > (existing.id || '')) {
            map.set(key, p);
        }
    }
    return Array.from(map.values());
};

// Format plan ID for display: "plan_meeting_pro_v1" → "Meeting Pro"
const formatPlanIdLabel = (planId) => {
    if (!planId) return '';
    return planId
        .replace('plan_', '')
        .replace(/_v\d+$/, '')
        .split('_')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

// Provider badge config: label, CSS class
const AUTH_PROVIDER_CONFIG = {
    email:     { label: 'Email',     className: 'provider-email' },
    google:    { label: 'Google',    className: 'provider-google' },
    microsoft: { label: 'Microsoft', className: 'provider-microsoft' },
    apple:     { label: 'Apple',     className: 'provider-apple' },
    facebook:  { label: 'Facebook',  className: 'provider-facebook' },
    zoom:      { label: 'Zoom',      className: 'provider-zoom' },
};

const getProviderDisplay = (provider) => {
    if (!provider) return null;
    return AUTH_PROVIDER_CONFIG[provider.toLowerCase()] || { label: provider, className: 'provider-unknown' };
};

const AdminSettings = () => {
    const usersLayout = useUsersLayout();
    // Which row's dropdowns have been opened. A closed <select> only ever displays its selected
    // option, so building the other N-1 for every row costs a full users x tenants (and
    // users x plans) DOM that nobody has looked at. Rows fill in on first interaction.
    const [openedOptionRows, setOpenedOptionRows] = useState(() => new Set());
    const markOptionsOpened = (rowKey) => setOpenedOptionRows((prev) => (
        prev.has(rowKey) ? prev : new Set(prev).add(rowKey)
    ));
    const navigate = useNavigate();
    const { user } = useAuth();
    // What the backend will honour for this caller: writes at platform level 4, the
    // SuperAdmin-only actions at 5. A control shown below its level is a guaranteed 403.
    const canWrite = canWritePlatformAdmin(user);
    const isSuperAdmin = isPlatformSuperAdmin(user);
    const [activeTab, setActiveTab] = useState('users'); // 'users', 'tenants', 'plans', or 'templates'
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Data State
    const [users, setUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [plans, setPlans] = useState([]);

    // Form State
    const [newTenantName, setNewTenantName] = useState('');

    // Quick create tenant modal state
    const [showCreateTenantModal, setShowCreateTenantModal] = useState(false);
    const [quickTenantName, setQuickTenantName] = useState('');

    // Per-user loading state for tenant updates
    const [updatingUserId, setUpdatingUserId] = useState(null);

    // Tenant filter state for users tab
    const [selectedTenantFilter, setSelectedTenantFilter] = useState('');
    const [selectedSignupPlatform, setSelectedSignupPlatform] = useState('');

    // Edit tenant modal state
    const [editingTenant, setEditingTenant] = useState(null);
    const [editTenantName, setEditTenantName] = useState('');
    const [editOrganizationName, setEditOrganizationName] = useState('');
    const [editLifecycleState, setEditLifecycleState] = useState('');
    const [editMeetingPlanId, setEditMeetingPlanId] = useState('');
    const [editWebinarPlanId, setEditWebinarPlanId] = useState('');

    // Delete tenant confirmation modal state
    const [deletingTenant, setDeletingTenant] = useState(null);

    // Delete user confirmation modal state
    const [deletingUser, setDeletingUser] = useState(null);
    const [deleteType, setDeleteType] = useState('soft'); // 'soft' or 'permanent'

    // Admin password reset modal state
    const [resettingPasswordUser, setResettingPasswordUser] = useState(null);
    const [tempPassword, setTempPassword] = useState('');
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState('');
    const [copiedTempPassword, setCopiedTempPassword] = useState(false);

    // Create user modal state
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [createUserEmail, setCreateUserEmail] = useState('');
    const [createUserPassword, setCreateUserPassword] = useState('');
    const [createUserName, setCreateUserName] = useState('');
    const [createUserTenantId, setCreateUserTenantId] = useState('');
    const [createUserShowPassword, setCreateUserShowPassword] = useState(false);
    const [isCreatingUser, setIsCreatingUser] = useState(false);

    // Show deleted users toggle state
    const [showDeletedUsers, setShowDeletedUsers] = useState(false);

    // Hide integration test data toggle state (hidden by default)
    const [hideTestData, setHideTestData] = useState(true);

    // Tenant search + plan filter state
    const [tenantSearch, setTenantSearch] = useState('');
    const [tenantPlanFilter, setTenantPlanFilter] = useState('All');

    // User search state
    const [userSearch, setUserSearch] = useState('');

    // Kebab menu and role dropdown state for users table
    const [openKebabId, setOpenKebabId] = useState(null);
    const [openRoleId, setOpenRoleId] = useState(null);

    // Phone card sheet for the role and tenant edits the table does inline:
    // { user, field: 'role' | 'tenant', value }
    const [accessEdit, setAccessEdit] = useState(null);
    const [isSavingAccess, setIsSavingAccess] = useState(false);
    // The page banner sits under the sheet's backdrop, so a refusal is shown inside the sheet.
    const [accessError, setAccessError] = useState('');
    // PIM tier sub-menu state: tracks which row's PIM tier sub-menu is open
    const [openPimSubmenuId, setOpenPimSubmenuId] = useState(null);

    // Create tenant panel state
    const [showCreatePanel, setShowCreatePanel] = useState(false);
    const [tenantNameError, setTenantNameError] = useState('');

    // Lifecycle state options
    const LIFECYCLE_STATES = [
        { value: 'active', label: 'Active' },
        { value: 'frozen', label: 'Frozen' },
        { value: 'disabled', label: 'Disabled' },
        { value: 'canceled', label: 'Canceled' }
    ];

    // Redirect if not a Platform Admin.
    // Sprint 5 fix: removed 'Admin' (tenant role) from the gate — tenant Admins always got 403
    // on platform operations. Only platform roles should access AdminSettings.
    useEffect(() => {
        if (user && !canReadPlatformAdmin(user)) {
            navigate('/host');
        }
    }, [user, navigate]);

    // Clear status after 5 seconds
    useEffect(() => {
        if (status.message) {
            const timer = setTimeout(() => setStatus({ type: '', message: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    // Close kebab/role dropdowns on outside click or Escape key
    useEffect(() => {
        const handleClickOutside = () => {
            setOpenKebabId(null);
            setOpenRoleId(null);
            setOpenPimSubmenuId(null);
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setOpenKebabId(null);
                setOpenRoleId(null);
                setOpenPimSubmenuId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            // Platform admins get all users (no tenantId); regular users get their tenant's users
            // Sprint 5 fix: 'Admin' (tenant role) removed — it does not have cross-tenant access
            const isPlatformAdmin = canReadPlatformAdmin(user);
            const baseUrl = `${process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api'}/GetUsers`;
            const url = isPlatformAdmin ? baseUrl : `${baseUrl}?tenantId=${encodeURIComponent(user?.TenantId || '')}`;
            const response = await fetchWithAuth(url, {
                method: 'GET'
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            } else {
                throw new Error('Failed to fetch users');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            trackException(error, { errorType: 'AdminSettings_FetchUsersFailed' });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTenants = async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api'}/superadmin/tenants`, {
                method: 'GET'
            });
            if (response.ok) {
                const data = await response.json();
                // AdminListTenants returns { count, tenants, queriedBy }.
                // Normalize to the legacy field shape (RowKey, TenantName) used throughout this component.
                const rawTenants = data.tenants || data;
                const normalized = rawTenants.map(t => ({
                    ...t,
                    RowKey: t.RowKey || t.tenantId,
                    TenantName: t.TenantName || t.name,
                    OrganizationName: t.OrganizationName ?? t.organizationName,
                    LifecycleState: t.LifecycleState || t.lifecycleState,
                    PlanTier: t.PlanTier ?? t.planTier,
                    PlanId: t.PlanId ?? t.planId,
                    WebinarPlanId: t.WebinarPlanId ?? t.webinarPlanId,
                    UserCount: t.UserCount ?? t.userCount,
                    CreatedAt: t.CreatedAt ?? t.createdAt
                }));
                setTenants(normalized);
            } else {
                throw new Error('Failed to fetch tenants');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
        } finally {
            setIsLoading(false);
        }
    };


    const fetchPlans = async () => {
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api'}/plans`,
                { method: 'GET' }
            );
            if (response.ok) {
                const data = await response.json();
                setPlans(data.plans || []);
            }
        } catch (error) {
            console.error('Failed to fetch plans:', error);
            trackException(error, { errorType: 'AdminSettings_FetchPlansFailed' });
        }
    };

    // Load tenants and plans on mount
    useEffect(() => {
        fetchTenants();
        fetchPlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (activeTab === 'users') fetchUsers();
        else if (activeTab === 'tenants') fetchTenants();
        // Plans tab now manages its own data fetching
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleRefresh = () => {
        if (activeTab === 'users') fetchUsers();
        else if (activeTab === 'tenants') fetchTenants();
        // Plans tab handles its own refresh
    };

    const handleUpdateUser = async (userToUpdate, updates) => {
        // Set loading state for this specific user if updating tenant
        const isUpdatingTenant = updates.tenantId !== undefined;
        if (isUpdatingTenant) {
            setUpdatingUserId(userToUpdate.RowKey);
        }
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api'}/UpdateUser`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    rowKey: userToUpdate.RowKey,
                    ...updates
                })
            });

            if (response.ok) {
                const updatedUser = await response.json();
                setStatus({ type: 'success', message: 'User updated successfully' });
                // Update the user in local state instead of refetching all users
                setUsers(prevUsers => prevUsers.map(u =>
                    u.RowKey === userToUpdate.RowKey
                        ? { ...u, ...updatedUser }
                        : u
                ));
            } else {
                const errorData = await response.json().catch(() => ({}));
                // UpdateUser writes its refusals as { error }; show the server's reason, not a generic line.
                throw new Error(errorData.error || errorData.message || 'Failed to update user');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            trackException(error, { errorType: 'AdminSettings_UpdateUserFailed' });
            throw error; // allow callers (e.g. PIM optimistic-revert) to .catch()
        } finally {
            setUpdatingUserId(null);
        }
    };

    const handleRemoveFreeTier = (userToUpdate) => {
        handleUpdateUser(userToUpdate, { isFreeTier: false }).catch(() => {});
    };

    const handleSetFreeTier = (userToUpdate) => {
        handleUpdateUser(userToUpdate, { isFreeTier: true }).catch(() => {});
    };

    const handleSaveAccessEdit = async () => {
        if (!accessEdit) return;
        const { user: target, field, value } = accessEdit;
        const updates = field === 'role'
            ? roleUpdate(value)
            : { tenantId: value, tenantName: tenants.find(t => t.RowKey === value)?.TenantName || '' };
        setIsSavingAccess(true);
        setAccessError('');
        try {
            await handleUpdateUser(target, updates);
            setAccessEdit(null);
        } catch (error) {
            // handleUpdateUser already tracked it; keep the sheet open so the operator can retry.
            setAccessError(error.message || 'Update failed');
        } finally {
            setIsSavingAccess(false);
        }
    };


    const handleCreateTenant = async (tenantName, isQuickCreate = false) => {
        const nameToUse = tenantName || newTenantName;

        // Validation
        if (!nameToUse.trim()) {
            if (!isQuickCreate) {
                setTenantNameError('Tenant name is required');
            }
            return;
        }
        if (nameToUse.trim().length < 2) {
            if (!isQuickCreate) {
                setTenantNameError('Name must be at least 2 characters');
            }
            return;
        }
        setTenantNameError('');

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api'}/superadmin/tenants`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: nameToUse })
            });

            if (response.ok) {
                setStatus({ type: 'success', message: 'Tenant created successfully' });
                if (isQuickCreate) {
                    setQuickTenantName('');
                    setShowCreateTenantModal(false);
                } else {
                    setNewTenantName('');
                    setShowCreatePanel(false);
                }
                fetchTenants();
            } else {
                throw new Error('Failed to create tenant');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            trackException(error, { errorType: 'AdminSettings_CreateTenantFailed' });
        }
    };

    const handleUpdateTenant = async () => {
        if (!editingTenant || !editTenantName.trim()) return;

        try {
            const tenantIdToUpdate = editingTenant.RowKey || editingTenant.tenantId;
            const baseUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';

            const originalMeetingPlanId = editingTenant.PlanId || editingTenant.planId || '';
            const originalWebinarPlanId = editingTenant.WebinarPlanId || editingTenant.webinarPlanId || '';
            const meetingPlanChanged = editMeetingPlanId !== originalMeetingPlanId;
            const webinarPlanChanged = editWebinarPlanId !== originalWebinarPlanId;

            // The catalog plan is the only editable plan source. The server resolves
            // its billing tier and product line inside the same document update.
            const response = await fetchWithAuth(`${baseUrl}/superadmin/tenants/${tenantIdToUpdate}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: editTenantName,
                    organizationName: editOrganizationName,
                    ...(meetingPlanChanged ? { meetingPlanId: editMeetingPlanId } : {}),
                    ...(webinarPlanChanged ? { webinarPlanId: editWebinarPlanId } : {})
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update tenant');
            }

            // If lifecycle state changed, send a separate call to the dedicated lifecycle endpoint.
            // AdminUpdateTenant does not accept lifecycleState — use PUT /superadmin/tenants/{id}/lifecycle.
            const originalLifecycleState = editingTenant.LifecycleState || 'active';
            if (editLifecycleState && editLifecycleState !== originalLifecycleState) {
                const lifecycleResponse = await fetchWithAuth(
                    `${baseUrl}/superadmin/tenants/${tenantIdToUpdate}/lifecycle`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ state: editLifecycleState })
                    }
                );
                if (!lifecycleResponse.ok) {
                    throw new Error('Tenant details saved, but failed to update lifecycle state');
                }
            }

            setStatus({ type: 'success', message: 'Tenant updated successfully' });
            setEditingTenant(null);
            setEditTenantName('');
            setEditOrganizationName('');
            setEditLifecycleState('');
            setEditMeetingPlanId('');
            setEditWebinarPlanId('');
            fetchTenants();
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            trackException(error, { errorType: 'AdminSettings_UpdateTenantFailed' });
        }
    };

    const handleDeleteUser = async () => {
        if (!deletingUser) return;

        try {
            const permanentParam = deleteType === 'permanent' ? '?permanent=true' : '';
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api'}/superadmin/tenants/${deletingUser.TenantId}/users/${deletingUser.RowKey}${permanentParam}`,
                {
                    method: 'DELETE'
                }
            );

            if (response.ok) {
                const successMessage = deleteType === 'permanent'
                    ? 'User permanently deleted'
                    : 'User soft-deleted successfully';
                setStatus({ type: 'success', message: successMessage });
                setDeletingUser(null);
                setDeleteType('soft'); // Reset to default
                fetchUsers();
            } else {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || 'Failed to delete user');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            trackException(error, { errorType: 'AdminSettings_DeleteUserFailed' });
        }
    };

    const handleDeleteTenant = async () => {
        if (!deletingTenant) return;

        try {
            const tenantIdToDelete = deletingTenant.RowKey || deletingTenant.tenantId;
            const response = await fetchWithAuth(`${process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api'}/superadmin/tenants/${tenantIdToDelete}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                setStatus({ type: 'success', message: 'Tenant deleted successfully' });
                setDeletingTenant(null);
                fetchTenants();
            } else {
                throw new Error('Failed to delete tenant');
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            trackException(error, { errorType: 'AdminSettings_DeleteTenantFailed' });
        }
    };

    const handleAdminResetPassword = async () => {
        if (!resettingPasswordUser) return;
        setIsResettingPassword(true);
        setResetPasswordError('');

        try {
            const apiUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';
            const response = await fetchWithAuth(`${apiUrl}/auth/admin-reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: resettingPasswordUser.RowKey,
                    tenantId: resettingPasswordUser.TenantId
                })
            });

            if (response.ok) {
                const data = await response.json();
                setTempPassword(data.temporaryPassword || '');
            } else {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                setResetPasswordError(errorData.error || 'Failed to reset password');
                trackException(new Error(errorData.error), { errorType: 'AdminSettings_AdminResetPasswordFailed' });
            }
        } catch (error) {
            setResetPasswordError('An unexpected error occurred');
            trackException(error, { errorType: 'AdminSettings_AdminResetPasswordFailed' });
        } finally {
            setIsResettingPassword(false);
        }
    };

    const handleCopyTempPassword = () => {
        if (tempPassword) {
            navigator.clipboard.writeText(tempPassword).catch(() => {});
            setCopiedTempPassword(true);
            setTimeout(() => setCopiedTempPassword(false), 2000);
        }
    };

    const closeResetPasswordModal = () => {
        setResettingPasswordUser(null);
        setTempPassword('');
        setResetPasswordError('');
        setIsResettingPassword(false);
        setCopiedTempPassword(false);
    };

    const handleCreateUser = async () => {
        if (!createUserEmail.trim() || !createUserPassword.trim()) return;

        setIsCreatingUser(true);
        try {
            const baseUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api';

            // Step 1: Register user via auth/register endpoint.
            // No skipAuth — forward the admin's JWT so the server can attribute the creation.
            const registerResponse = await fetchWithAuth(`${baseUrl}/auth/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: createUserEmail,
                    password: createUserPassword,
                    name: createUserName || ''
                })
            });

            if (!registerResponse.ok) {
                const errorData = await registerResponse.json().catch(() => ({ error: 'Registration failed' }));
                throw new Error(errorData.error || errorData.message || 'Failed to register user');
            }

            const registerData = await registerResponse.json();
            // Server returns { User: { UserId: ... } } — PascalCase shape from AuthFunctions.cs
            const newUserId = registerData.User?.UserId;
            if (createUserTenantId && !newUserId) {
                throw new Error('User created but UserId missing from response — cannot assign tenant');
            }

            // Step 2: If a tenant was selected, update the user's tenant assignment
            if (createUserTenantId && newUserId) {
                const selectedTenant = tenants.find(t => t.RowKey === createUserTenantId);
                const updateResponse = await fetchWithAuth(`${baseUrl}/UpdateUser`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        rowKey: newUserId,
                        tenantId: createUserTenantId,
                        tenantName: selectedTenant ? selectedTenant.TenantName : ''
                    })
                });

                if (!updateResponse.ok) {
                    // User was created but tenant assignment failed — surface as warning, not success,
                    // and emit telemetry so App Insights alerts can fire.
                    const errData = await updateResponse.json().catch(() => ({}));
                    const assignError = new Error(errData.error || 'Tenant assignment failed');
                    trackException(assignError, { errorType: 'AdminSettings_CreateUser_TenantAssignFailed' });
                    setStatus({ type: 'warning', message: 'User created, but tenant assignment failed. Update tenant manually.' });
                    setShowCreateUserModal(false);
                    resetCreateUserForm();
                    fetchUsers();
                    return;
                }
            }

            setStatus({ type: 'success', message: `User ${createUserEmail} created successfully` });
            setShowCreateUserModal(false);
            resetCreateUserForm();
            fetchUsers();
            // If no tenant was selected, the backend auto-creates one — refresh tenants
            // so the new tenant appears in dropdowns immediately.
            if (!createUserTenantId) {
                fetchTenants();
            }
        } catch (error) {
            setStatus({ type: 'error', message: error.message });
            trackException(error, { errorType: 'AdminSettings_CreateUserFailed' });
        } finally {
            setIsCreatingUser(false);
        }
    };

    const resetCreateUserForm = () => {
        setCreateUserEmail('');
        setCreateUserPassword('');
        setCreateUserName('');
        setCreateUserTenantId('');
        setCreateUserShowPassword(false);
    };

    // Client-side password validation matching backend policy
    const isPasswordValid = (pw) => {
        return pw.length >= 8 &&
            /[A-Z]/.test(pw) &&
            /[a-z]/.test(pw) &&
            /[0-9]/.test(pw);
    };

    // IsFreeTier=false is the billing bypass (root CLAUDE.md §9); everyone else shows the plan lines
    // their tenant holds, as resolved by GetUsers. A null PlanTier means the plan could not be read.
    const getTierDisplay = (u) => {
        if (u.IsFreeTier === false) return { label: 'Unlimited', className: 'tier-unlimited' };
        if (!u.PlanTier) return { label: '—', className: 'tier-unknown' };
        if (u.PlanTier === 'Free') return { label: 'Free', className: 'tier-free' };
        return { label: u.PlanTier, className: 'tier-plan' };
    };

    // Helper to copy email to clipboard
    const handleCopyEmail = async (e, email) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(email);
            setStatus({ type: 'success', message: 'Email copied to clipboard' });
        } catch (clipboardError) {
            trackException(clipboardError, { errorType: 'AdminSettings_CopyEmailFailed' });
            setStatus({ type: 'error', message: 'Could not copy email to clipboard' });
        }
    };

    // Helper to get CSS class for role badge
    const getRoleBadgeClass = (role) => {
        const r = (role || '').toLowerCase().replace(/\s/g, '');
        if (r === 'superadmin') return 'role-badge-superadmin';
        if (r === 'owner') return 'role-badge-owner';
        if (r === 'admin') return 'role-badge-admin';
        if (r === 'member') return 'role-badge-member';
        return 'role-badge-viewer';
    };

    // Filtered users list (shared between desktop table and mobile cards)
    const filteredUsers = users
        .filter(u => !selectedTenantFilter || u.TenantId === selectedTenantFilter)
        .filter(u => !selectedSignupPlatform || signupPlatform(u) === selectedSignupPlatform)
        .filter(u => showDeletedUsers || u.Status !== 'deleted')
        .filter(u => !hideTestData || !isIntegrationTestData(u))
        .filter(u => {
            if (!userSearch.trim()) return true;
            const q = userSearch.toLowerCase();
            return (
                (u.Name || '').toLowerCase().includes(q) ||
                (u.Email || '').toLowerCase().includes(q)
            );
        });

    // Render a single user row (desktop table)
    const renderUserRow = (u) => {
        const isDeleted = u.Status === 'deleted';
        const tier = getTierDisplay(u);
        const kebabOpen = openKebabId === u.RowKey;
        const roleOpen = openRoleId === u.RowKey;
        const provDisplay = getProviderDisplay(u.AuthProvider);

        return (
            <tr key={u.RowKey}>
                {/* User (Name + Email + Provider merged) */}
                <td>
                    <div className="user-identity-cell">
                        <span className="user-name-text">{u.Name}</span>
                        <span className="user-email-text">
                            <span>{u.Email}</span>
                            <button
                                type="button"
                                className="copy-email-btn"
                                onClick={(e) => handleCopyEmail(e, u.Email)}
                                aria-label={`Copy ${u.Email}`}
                                data-tooltip="Copy email"
                            >
                                <Copy size={11} />
                            </button>
                        </span>
                        {provDisplay && (
                            <span className={`provider-badge ${provDisplay.className}`}>{provDisplay.label}</span>
                        )}
                        <span className="user-email-text">Signup: {SIGNUP_PLATFORMS[signupPlatform(u)]}</span>
                    </div>
                </td>
                {/* Role (clickable badge; a plain badge for a platform reader, who never writes) */}
                <td>
                    {!canWrite ? (
                        <span className={`role-badge-btn ${getRoleBadgeClass(u.Role)}`}>{u.Role || 'No role'}</span>
                    ) : (
                    <div className="role-badge-wrap" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className={`role-badge-btn ${getRoleBadgeClass(u.Role)}`}
                            onClick={() => setOpenRoleId(roleOpen ? null : u.RowKey)}
                            aria-haspopup="listbox"
                            aria-expanded={roleOpen}
                            aria-label={`Role: ${u.Role || 'No role'}. Activate to change.`}
                        >
                            {u.Role || 'No role'}
                        </button>
                        {roleOpen && (
                            <div className="role-popover" role="listbox" aria-label="Select role">
                                {getRoleChangeBlockedReason(user, u) && (
                                    <div className="role-popover-note">{getRoleChangeBlockedReason(user, u)}</div>
                                )}
                                {getAssignableRoles(user, u).map(role => (
                                    <button
                                        key={role.value}
                                        type="button"
                                        role="option"
                                        aria-selected={role.value === u.Role}
                                        className={`role-popover-item ${role.value === u.Role ? 'active' : ''}`}
                                        onClick={() => {
                                            handleUpdateUser(u, roleUpdate(role.value)).catch(() => {});
                                            setOpenRoleId(null);
                                        }}
                                    >
                                        {role.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    )}
                </td>
                {/* Tier */}
                <td>
                    <span className={`tier-badge-unified ${tier.className}`}>
                        {tier.className === 'tier-unlimited' && <Infinity size={12} />}
                        {tier.className === 'tier-free' && <Clock size={12} />}
                        <span>{tier.label}</span>
                    </span>
                </td>
                {/* Tenant / Plan (merged) */}
                <td className="tenant-plan-merged-cell">
                    <div className="tenant-select-wrapper"
                         data-tooltip={tenants.find(t => t.RowKey === u.TenantId)?.TenantName || 'No Tenant'}
                    >
                        {updatingUserId === u.RowKey ? (
                            <div className="tenant-loading">
                                <Loader2 size={16} className="animate-spin" />
                                <span>Updating...</span>
                            </div>
                        ) : !canWrite ? (
                            <span className="tenant-readonly">
                                {tenants.find(t => t.RowKey === u.TenantId)?.TenantName || 'No Tenant'}
                            </span>
                        ) : (
                            <>
                                <select
                                    className="ui-select sm editable-dropdown"
                                    value={u.TenantId || ''}
                                    onFocus={() => markOptionsOpened(u.RowKey)}
                                    onPointerDown={() => markOptionsOpened(u.RowKey)}
                                    onChange={(e) => {
                                        const selectedTenant = tenants.find(t => t.RowKey === e.target.value);
                                        handleUpdateUser(u, {
                                            tenantId: e.target.value,
                                            tenantName: selectedTenant ? selectedTenant.TenantName : ''
                                        });
                                    }}
                                >
                                    <option value="">No Tenant</option>
                                    {(openedOptionRows.has(u.RowKey)
                                        ? tenants
                                        : tenants.filter(t => t.RowKey === u.TenantId)
                                    ).map(t => (
                                        <option key={t.RowKey} value={t.RowKey}>{t.TenantName}</option>
                                    ))}
                                </select>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setShowCreateTenantModal(true)}
                                    data-tooltip="Create New Tenant"
                                    className="add-tenant-btn"
                                >
                                    <Plus size={14} />
                                </Button>
                            </>
                        )}
                    </div>
                    <span className="plan-sub">
                        {u.TenantId === 'c2hl' && canWrite ? (
                            <select
                                className="ui-select sm editable-dropdown plan-inline-select"
                                value={u.PlanId || ''}
                                onFocus={() => markOptionsOpened(u.RowKey)}
                                onPointerDown={() => markOptionsOpened(u.RowKey)}
                                onChange={(e) => handleUpdateUser(u, { planId: e.target.value || '' }).catch(() => {})}
                            >
                                <option value="">Tenant default</option>
                                {(openedOptionRows.has(u.RowKey)
                                    ? deduplicatePlans(plans.filter(p => p.active))
                                    : deduplicatePlans(plans.filter(p => p.active)).filter(p => p.id === u.PlanId)
                                ).map(p => (
                                    <option key={p.id} value={p.id}>
                                        {formatPlanLabel(p)}
                                    </option>
                                ))}
                            </select>
                        ) : u.PlanId ? (
                            <span className={`plan-sub-badge tier-badge tier-${(plans.find(p => p.id === u.PlanId)?.tier || 'free').toLowerCase()}`}>
                                {formatPlanIdLabel(u.PlanId)}
                            </span>
                        ) : (
                            <span className="plan-sub-none">No plan override</span>
                        )}
                    </span>
                </td>
                {/* PIM Tier */}
                <td className="pim-tier-cell" data-testid={`pim-tier-cell-${u.RowKey}`}>
                    {u.pimTier ? (
                        <span className={`pim-tier-pill pim-tier-${u.pimTier.toLowerCase()}`} aria-label={`PIM Tier: ${u.pimTier}`}>
                            {u.pimTier}
                        </span>
                    ) : (
                        <span className="pim-tier-unset" aria-label="No PIM tier set">—</span>
                    )}
                </td>
                {/* Actions (kebab) */}
                <td className="kebab-cell">
                    {isDeleted ? (
                        <span className="status-badge status-deleted">Deleted</span>
                    ) : !canWrite ? null : (
                        <div className="kebab-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="kebab-trigger"
                                onClick={() => setOpenKebabId(kebabOpen ? null : u.RowKey)}
                                aria-label={`Actions for ${u.Name || u.Email}`}
                                aria-haspopup="menu"
                                aria-expanded={kebabOpen}
                            >
                                <MoreVertical size={16} />
                            </button>
                            {kebabOpen && (
                                <div className="kebab-menu" role="menu" aria-label={`Actions for ${u.Name || u.Email}`}>
                                    {u.IsFreeTier === true ? (
                                        <>
                                            <button type="button" role="menuitem" className="kebab-item" onClick={() => { handleRemoveFreeTier(u); setOpenKebabId(null); }}>
                                                <Infinity size={14} /> Make Unlimited
                                            </button>
                                        </>
                                    ) : (
                                        !PRIVILEGED_ROLES.includes(u.Role) && (
                                            <button type="button" role="menuitem" className="kebab-item" onClick={() => { handleSetFreeTier(u); setOpenKebabId(null); }}>
                                                <Clock size={14} /> Set Free Tier
                                            </button>
                                        )
                                    )}
                                    {/* PIM Tier sub-menu */}
                                    <div className="kebab-submenu-wrap">
                                        <button
                                            type="button"
                                            role="menuitem"
                                            className="kebab-item kebab-item-submenu"
                                            onClick={(e) => { e.stopPropagation(); setOpenPimSubmenuId(openPimSubmenuId === u.RowKey ? null : u.RowKey); }}
                                            aria-haspopup="menu"
                                            aria-expanded={openPimSubmenuId === u.RowKey}
                                        >
                                            <Tag size={14} /> Set PIM Tier
                                        </button>
                                        {openPimSubmenuId === u.RowKey && (
                                            <div className="kebab-submenu" role="menu">
                                                {SUPPORTED_TIERS.map(tier => (
                                                    <button
                                                        key={tier}
                                                        type="button"
                                                        role="menuitem"
                                                        className="kebab-item"
                                                        data-pim-tier-option={tier}
                                                        onClick={() => {
                                                            // Capture previous value at click time (not from stale closure)
                                                            const previousTier = u.pimTier;
                                                            // Optimistic update: immediately flip the cell
                                                            setUsers(prev => prev.map(usr => usr.RowKey === u.RowKey ? { ...usr, pimTier: tier } : usr));
                                                            setOpenKebabId(null);
                                                            setOpenPimSubmenuId(null);
                                                            handleUpdateUser(u, { pimTier: tier }).catch(() => {
                                                                // Revert to the value captured at click time
                                                                setUsers(prev => prev.map(usr => usr.RowKey === u.RowKey ? { ...usr, pimTier: previousTier } : usr));
                                                            });
                                                        }}
                                                    >
                                                        {tier}
                                                    </button>
                                                ))}
                                                {u.pimTier && (
                                                    <>
                                                        <div className="kebab-divider" role="separator" />
                                                        <button
                                                            type="button"
                                                            role="menuitem"
                                                            className="kebab-item kebab-item-danger"
                                                            data-pim-tier-option="remove"
                                                            onClick={() => {
                                                                // Capture previous value at click time (not from stale closure)
                                                                const previousTier = u.pimTier;
                                                                setUsers(prev => prev.map(usr => usr.RowKey === u.RowKey ? { ...usr, pimTier: null } : usr));
                                                                setOpenKebabId(null);
                                                                setOpenPimSubmenuId(null);
                                                                handleUpdateUser(u, { pimTier: null }).catch(() => {
                                                                    // Revert to the value captured at click time
                                                                    setUsers(prev => prev.map(usr => usr.RowKey === u.RowKey ? { ...usr, pimTier: previousTier } : usr));
                                                                });
                                                            }}
                                                        >
                                                            Remove PIM tier
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {canWrite && u.AuthProvider === 'email' && (
                                        <button type="button" role="menuitem" className="kebab-item" onClick={() => { setResettingPasswordUser(u); setOpenKebabId(null); }}>
                                            <KeyRound size={14} /> Reset Password
                                        </button>
                                    )}
                                    {isSuperAdmin && (
                                        <>
                                            <div className="kebab-divider" role="separator" />
                                            <button
                                                type="button"
                                                role="menuitem"
                                                className="kebab-item kebab-item-danger"
                                                onClick={() => { setDeletingUser(u); setOpenKebabId(null); }}
                                            >
                                                <Trash2 size={14} /> Delete User
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </td>
            </tr>
        );
    };

    // Render a single user card (mobile)
    const renderUserCard = (u) => {
        const tier = getTierDisplay(u);
        const isDeleted = u.Status === 'deleted';
        const kebabOpen = openKebabId === u.RowKey;
        const provDisplay = getProviderDisplay(u.AuthProvider);

        return (
            <div key={u.RowKey} className="user-card">
                <div className="user-card-main">
                    <div className="user-card-row1">
                        <span className="user-name-text">{u.Name}</span>
                        <span className={`role-badge-btn ${getRoleBadgeClass(u.Role)}`} style={{ fontSize: '0.625rem', padding: '0.15rem 0.375rem' }}>
                            {u.Role}
                        </span>
                    </div>
                    <div className="user-card-email">
                        <span>{u.Email}</span>
                        <button
                            type="button"
                            className="copy-email-btn copy-email-btn-visible"
                            onClick={(e) => handleCopyEmail(e, u.Email)}
                            aria-label={`Copy ${u.Email}`}
                        >
                            <Copy size={11} />
                        </button>
                        {provDisplay && (
                            <span className={`provider-badge ${provDisplay.className}`}>{provDisplay.label}</span>
                        )}
                    </div>
                    <div className="user-email-text">Signup: {SIGNUP_PLATFORMS[signupPlatform(u)]}</div>
                    <div className="user-card-meta">
                        <span className={`tier-badge-unified ${tier.className}`}>
                            {tier.className === 'tier-unlimited' && <Infinity size={10} />}
                            {tier.className === 'tier-free' && <Clock size={10} />}
                            <span>{tier.label}</span>
                        </span>
                        {u.pimTier && (
                            <span className={`pim-tier-pill pim-tier-${u.pimTier.toLowerCase()}`} style={{ fontSize: '0.625rem', padding: '0.1rem 0.4rem' }} aria-label={`PIM Tier: ${u.pimTier}`}>
                                {u.pimTier}
                            </span>
                        )}
                        <span className="user-card-tenant">
                            {tenants.find(t => t.RowKey === u.TenantId)?.TenantName || 'No Tenant'}
                        </span>
                    </div>
                </div>
                <div className="user-card-actions">
                    {isDeleted ? (
                        <span className="status-badge status-deleted">Deleted</span>
                    ) : !canWrite ? null : (
                        <div className="kebab-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="kebab-trigger"
                                onClick={() => setOpenKebabId(kebabOpen ? null : u.RowKey)}
                                aria-label={`Actions for ${u.Name || u.Email}`}
                                aria-haspopup="menu"
                                aria-expanded={kebabOpen}
                            >
                                <MoreVertical size={16} />
                            </button>
                            {kebabOpen && (
                                <div className="kebab-menu" role="menu" aria-label={`Actions for ${u.Name || u.Email}`}>
                                    {/* The table edits these inline; a card has no room, so they open a sheet. */}
                                    <button type="button" role="menuitem" className="kebab-item" onClick={() => { setAccessEdit({ user: u, field: 'role', value: u.Role || '' }); setOpenKebabId(null); }}>
                                        <UserCog size={14} /> Change role
                                    </button>
                                    {canWrite && (
                                    <button type="button" role="menuitem" className="kebab-item" onClick={() => { setAccessEdit({ user: u, field: 'tenant', value: u.TenantId || '' }); setOpenKebabId(null); }}>
                                        <ArrowRightLeft size={14} /> Change tenant
                                    </button>
                                    )}
                                    {canWrite && <div className="kebab-divider" role="separator" />}
                                    {!canWrite ? null : u.IsFreeTier === true ? (
                                        <>
                                            <button type="button" role="menuitem" className="kebab-item" onClick={() => { handleRemoveFreeTier(u); setOpenKebabId(null); }}>
                                                <Infinity size={14} /> Make Unlimited
                                            </button>
                                        </>
                                    ) : (
                                        !PRIVILEGED_ROLES.includes(u.Role) && (
                                            <button type="button" role="menuitem" className="kebab-item" onClick={() => { handleSetFreeTier(u); setOpenKebabId(null); }}>
                                                <Clock size={14} /> Set Free Tier
                                            </button>
                                        )
                                    )}
                                    {canWrite && u.AuthProvider === 'email' && (
                                        <button type="button" role="menuitem" className="kebab-item" onClick={() => { setResettingPasswordUser(u); setOpenKebabId(null); }}>
                                            <KeyRound size={14} /> Reset Password
                                        </button>
                                    )}
                                    {isSuperAdmin && (
                                        <>
                                            <div className="kebab-divider" role="separator" />
                                            <button
                                                type="button"
                                                role="menuitem"
                                                className="kebab-item kebab-item-danger"
                                                onClick={() => { setDeletingUser(u); setOpenKebabId(null); }}
                                            >
                                                <Trash2 size={14} /> Delete User
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="admin-container">
            <div className="admin-header">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" onClick={() => navigate('/host')} data-tooltip="Back to Dashboard">
                        <ArrowLeft size={24} />
                    </Button>
                    <h1>Admin Settings</h1>
                </div>
                <div className="settings-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        <Users size={18} /> Users
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'tenants' ? 'active' : ''}`}
                        onClick={() => setActiveTab('tenants')}
                    >
                        <Building size={18} /> Tenants
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'plans' ? 'active' : ''}`}
                        onClick={() => setActiveTab('plans')}
                    >
                        <DollarSign size={18} /> Plans
                    </button>
                    {/* Its own tab rather than another card on Plans: this one is scoped to ONE
                        searched tenant and is the only screen here that writes credits, so it
                        must not load or render alongside the platform-wide rate editor. */}
                    <button
                        className={`tab-btn ${activeTab === 'usage-ledger' ? 'active' : ''}`}
                        onClick={() => setActiveTab('usage-ledger')}
                    >
                        <Coins size={18} /> Usage Ledger
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'templates' ? 'active' : ''}`}
                        onClick={() => setActiveTab('templates')}
                    >
                        <MessageSquare size={18} /> Templates
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'plan-editor' ? 'active' : ''}`}
                        onClick={() => setActiveTab('plan-editor')}
                    >
                        <Pencil size={18} /> Plan Editor
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'admin-tools' ? 'active' : ''}`}
                        onClick={() => setActiveTab('admin-tools')}
                    >
                        <Users size={18} /> Admin Tools
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'model-policy' ? 'active' : ''}`}
                        onClick={() => setActiveTab('model-policy')}
                    >
                        <Sliders size={18} /> Model Policy
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'nonprofit' ? 'active' : ''}`}
                        onClick={() => setActiveTab('nonprofit')}
                        data-testid="admin-tab-nonprofit"
                    >
                        <HeartHandshake size={18} /> Nonprofit
                    </button>
                    <button
                        className="tab-btn"
                        onClick={() => navigate('/glossary', { state: { from: '/admin-settings' } })}
                    >
                        <BookOpen size={18} /> Glossaries
                    </button>
                    <button
                        className="tab-btn"
                        onClick={() => navigate('/prompt-settings')}
                    >
                        <BookOpen size={18} /> Prompt Settings
                    </button>
                </div>
            </div>

            {status.message && (
                <div className={`status-message ${status.type} mb-4`}>
                    {status.type === 'error' ? <AlertCircle size={20} /> : <CheckCircle size={20} />}
                    {status.message}
                </div>
            )}

            <div className="admin-content">
                {isLoading ? (
                    <div className="loading-state">
                        <Loader2 className="animate-spin" size={48} />
                        <p>Loading...</p>
                    </div>
                ) : activeTab === 'users' ? (
                    <div className="users-list">
                        <div className="content-toolbar">
                            <div className="toolbar-left">
                                <label className="filter-label">Tenant:</label>
                                <select
                                    className="ui-select tenant-filter"
                                    value={selectedTenantFilter}
                                    onChange={(e) => setSelectedTenantFilter(e.target.value)}
                                    title="Filter by tenant"
                                >
                                    <option value="">All Tenants</option>
                                    {tenants.map(t => (
                                        <option key={t.RowKey} value={t.RowKey}>{t.TenantName}</option>
                                    ))}
                                </select>
                                <label className="filter-label" htmlFor="signup-platform-filter">Signup platform</label>
                                <select
                                    id="signup-platform-filter"
                                    className="ui-select"
                                    value={selectedSignupPlatform}
                                    onChange={(e) => setSelectedSignupPlatform(e.target.value)}
                                >
                                    <option value="">All platforms</option>
                                    {Object.entries(SIGNUP_PLATFORMS).map(([value, label]) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                                <div className="filter-controls-group">
                                    <label className="filter-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={showDeletedUsers}
                                            onChange={(e) => setShowDeletedUsers(e.target.checked)}
                                        />
                                        <span>Show deleted</span>
                                    </label>
                                    <label className="filter-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={hideTestData}
                                            onChange={(e) => setHideTestData(e.target.checked)}
                                        />
                                        <span>Hide test data</span>
                                    </label>
                                </div>
                            </div>
                            <div className="toolbar-right">
                                {canWrite && (
                                <Button
                                    variant="primary"
                                    icon={<UserPlus size={16} />}
                                    onClick={() => setShowCreateUserModal(true)}
                                    className="create-user-trigger"
                                >
                                    New User
                                </Button>
                                )}
                                <div className="tenant-search-wrap">
                                    <Search size={14} className="tenant-search-icon" />
                                    <input
                                        className="tenant-search-input"
                                        type="text"
                                        placeholder="Search users…"
                                        value={userSearch}
                                        onChange={(e) => setUserSearch(e.target.value)}
                                    />
                                </div>
                                <button
                                    className="refresh-icon-btn"
                                    onClick={handleRefresh}
                                    disabled={isLoading}
                                    aria-label="Refresh"
                                    data-tooltip="Refresh"
                                >
                                    <RefreshCw size={20} className={isLoading ? 'rotating' : ''} />
                                </button>
                            </div>
                        </div>
                        {/* Desktop Table (6 columns) */}
                        {usersLayout === 'table' && (
                        <div className="users-table-desktop">
                            <table className="admin-table users-redesigned-table">
                                <thead>
                                    <tr>
                                        <th className="col-user">User</th>
                                        <th className="col-role-v2">Role</th>
                                        <th className="col-tier-v2">Tier</th>
                                        <th className="col-tenant-plan-v2">Tenant / Plan</th>
                                        <th className="col-pim-tier">PIM Tier</th>
                                        <th className="col-kebab"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="empty-state">No users found</td>
                                        </tr>
                                    ) : (
                                        filteredUsers.map(renderUserRow)
                                    )}
                                </tbody>
                            </table>
                        </div>
                        )}

                        {/* Mobile Card Layout */}
                        {usersLayout === 'cards' && (
                        <div className="users-card-list">
                            {filteredUsers.map(renderUserCard)}
                            {filteredUsers.length === 0 && (
                                <div className="empty-state">No users found</div>
                            )}
                        </div>
                        )}
                    </div>
                ) : activeTab === 'tenants' ? (
                    <div className="tenants-list">
                        {/* Toolbar with New Tenant button and refresh */}
                        <div className="content-toolbar">
                            {canWrite && (
                            <Button
                                variant="primary"
                                icon={<Plus size={16} />}
                                onClick={() => {
                                    setShowCreatePanel(!showCreatePanel);
                                    setTenantNameError('');
                                }}
                                className="create-tenant-trigger"
                            >
                                New Tenant
                            </Button>
                            )}
                            <button
                                className="refresh-icon-btn"
                                onClick={handleRefresh}
                                disabled={isLoading}
                                aria-label="Refresh"
                                data-tooltip="Refresh"
                            >
                                <RefreshCw size={20} className={isLoading ? 'rotating' : ''} />
                            </button>
                        </div>

                        {/* Animated slide-down create panel */}
                        {showCreatePanel && (
                            <div className="create-tenant-panel">
                                <div className="form-group">
                                    <label htmlFor="new-tenant-name">Tenant Name</label>
                                    <Input
                                        id="new-tenant-name"
                                        placeholder="e.g., Acme Corporation"
                                        value={newTenantName}
                                        onChange={(e) => {
                                            setNewTenantName(e.target.value);
                                            if (tenantNameError) setTenantNameError('');
                                        }}
                                        error={tenantNameError}
                                    />
                                    {tenantNameError && <span className="field-error">{tenantNameError}</span>}
                                </div>
                                <div className="panel-actions">
                                    <Button variant="ghost" onClick={() => {
                                        setShowCreatePanel(false);
                                        setNewTenantName('');
                                        setTenantNameError('');
                                    }}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={() => handleCreateTenant()}
                                        disabled={!newTenantName.trim()}
                                    >
                                        Create Tenant
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Toolbar with search, plan filters, and test data toggle */}
                        <div className="tenants-toolbar-v2">
                            <div className="tenants-toolbar-left">
                                <label className="toolbar-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={hideTestData}
                                        onChange={(e) => setHideTestData(e.target.checked)}
                                    />
                                    Hide test data
                                </label>
                            </div>
                            <div className="tenants-toolbar-right">
                                <div className="tenant-search-wrap">
                                    <Search size={14} className="tenant-search-icon" />
                                    <input
                                        className="tenant-search-input"
                                        type="text"
                                        placeholder="Search tenants…"
                                        value={tenantSearch}
                                        onChange={(e) => setTenantSearch(e.target.value)}
                                    />
                                </div>
                                <div className="plan-filter-group">
                                    {['All', 'Free', 'Starter', 'Pro', 'Enterprise'].map(plan => (
                                        <button
                                            key={plan}
                                            className={`plan-filter-btn plan-filter-${plan.toLowerCase()} ${tenantPlanFilter === plan ? 'active' : ''}`}
                                            onClick={() => setTenantPlanFilter(plan)}
                                        >
                                            {plan}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <table className="admin-table tenants-table">
                            <thead>
                                <tr>
                                    <th>Tenant Name</th>
                                    <th>Organization</th>
                                    <th>Status</th>
                                    <th>Plan</th>
                                    <th>Users</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tenants
                                    .filter(t => !hideTestData || !isIntegrationTestData(t))
                                    .filter(t => tenantPlanFilter === 'All' || (t.PlanTier || 'Free').toLowerCase() === tenantPlanFilter.toLowerCase())
                                    .filter(t => {
                                        if (!tenantSearch.trim()) return true;
                                        const q = tenantSearch.toLowerCase();
                                        return (
                                            (t.TenantName || '').toLowerCase().includes(q) ||
                                            (t.OrganizationName || '').toLowerCase().includes(q) ||
                                            (t.RowKey || '').toLowerCase().includes(q)
                                        );
                                    })
                                    .map(t => (
                                    <tr key={t.RowKey}>
                                        <td>
                                            <div className="tenant-name-cell">
                                                <span className="tenant-name">{t.TenantName}</span>
                                                <span className="tenant-id">{t.RowKey}</span>
                                            </div>
                                        </td>
                                        <td>
                                            {t.OrganizationName ? (
                                                t.OrganizationName
                                            ) : (
                                                <span className="empty-value">—</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`status-badge status-${t.LifecycleState || 'active'}`}>
                                                {t.LifecycleState || 'active'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="tenant-plan-cell">
                                                <span className={`tier-badge tier-${(t.PlanTier || 'free').toLowerCase()}`}>
                                                    {t.PlanTier || 'Free'}
                                                </span>
                                                {t.PlanId && (
                                                    <span className="plan-id-label" data-tooltip={t.PlanId}>
                                                        Meeting: {formatPlanIdLabel(t.PlanId)}
                                                    </span>
                                                )}
                                                {t.WebinarPlanId && (
                                                    <span className="plan-id-label" data-tooltip={t.WebinarPlanId}>
                                                        Webinar: {formatPlanIdLabel(t.WebinarPlanId)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-center">{t.UserCount || 0}</td>
                                        <td>{t.CreatedAt ? new Date(t.CreatedAt).toLocaleDateString() : '-'}</td>
                                        <td className="actions-cell">
                                            <div className="action-buttons">
                                                {canWrite && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    aria-label={`Edit Tenant ${t.TenantName || t.RowKey}`}
                                                    onClick={() => {
                                                        const currentMeetingPlanId = t.PlanId || t.planId || '';
                                                        const currentWebinarPlanId = t.WebinarPlanId || t.webinarPlanId || '';
                                                        setEditingTenant(t);
                                                        setEditTenantName(t.TenantName || '');
                                                        setEditOrganizationName(t.OrganizationName || '');
                                                        setEditLifecycleState(t.LifecycleState || 'active');
                                                        setEditMeetingPlanId(currentMeetingPlanId);
                                                        setEditWebinarPlanId(currentWebinarPlanId);
                                                    }}
                                                    data-tooltip="Edit Tenant"
                                                >
                                                    <Pencil size={14} />
                                                </Button>
                                                )}
                                                {isSuperAdmin && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="delete-btn"
                                                    aria-label={`Delete Tenant ${t.TenantName || t.RowKey}`}
                                                    onClick={() => setDeletingTenant(t)}
                                                    data-tooltip="Delete Tenant"
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : activeTab === 'usage-ledger' ? (
                    /* Usage Ledger - one tenant's credit ledger + operator adjustments.
                       `role` decides which controls exist at all: the ledger reads at platform
                       level 3 and adjusts at level 4, matching UsageLedgerAdminFunctions.cs. */
                    <div style={{ padding: 24 }}>
                        <UsageLedgerSection role={user?.Role} actor={user?.Email} tenants={tenants} />
                    </div>
                ) : activeTab === 'templates' ? (
                    /* Templates Tab - Question Template Management */
                    <TemplatesManagement readOnly={!canWrite} />
                ) : activeTab === 'plan-editor' ? (
                    /* Plan Editor - edit plan names, prices, limits */
                    <PlanEditor readOnly={!canWrite} />
                ) : activeTab === 'admin-tools' ? (
                    /* Admin Tools - tier distribution + unlimited assignment + Grafana link */
                    <div style={{ padding: 24, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                        <TierDistribution />
                        {isSuperAdmin && <UnlimitedPlanAssignment />}
                        <RealtimeProviderPolicySettings readOnly={!canWrite} />
                        <ClientSwitchesSettings readOnly={!canWrite} />
                        {process.env.REACT_APP_GRAFANA_URL && (
                            <a
                                href={`${process.env.REACT_APP_GRAFANA_URL}/dashboards?tag=product-analytics`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="grafana-link-card"
                            >
                                <BarChart3 size={20} className="grafana-link-card-icon" />
                                <span className="grafana-link-card-text">
                                    <span className="grafana-link-card-title">Product analytics dashboards</span>
                                    <span className="grafana-link-card-desc">Live iOS + web event charts in Grafana</span>
                                </span>
                            </a>
                        )}
                        {process.env.REACT_APP_GRAFANA_URL && (
                            <a
                                href={`${process.env.REACT_APP_GRAFANA_URL}/d/meetingsync-signup-platform-conversion`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="grafana-link-card"
                            >
                                <BarChart3 size={20} className="grafana-link-card-icon" />
                                <span className="grafana-link-card-text">
                                    <span className="grafana-link-card-title">Signup platform conversion</span>
                                    <span className="grafana-link-card-desc">Signup cohorts, trial subscriptions and first paid conversion</span>
                                </span>
                            </a>
                        )}
                    </div>
                ) : activeTab === 'nonprofit' ? (
                    /* Nonprofit & education discount applications (Backlog row 23): read level 3, decide level 4 */
                    <div style={{ padding: 24 }}>
                        <NonprofitApplicationsQueue readOnly={!canWrite} />
                    </div>
                ) : activeTab === 'model-policy' ? (
                    /* Model Policy - which model serves which translation slot, engine-owned */
                    <div style={{ padding: 24 }}>
                        <ModelPolicySection readOnly={!canWrite} />
                    </div>
                ) : (
                    /* Plans Tab - Using new card-based layout, plus PIM tier overrides */
                    <>
                        <MeetingLimitsSection readOnly={!canWrite} />
                        <UsageCatalogSection readOnly={!canWrite} />
                        <PlansManagement readOnly={!canWrite} />
                        <PimTiersSection readOnly={!canWrite} />
                    </>
                )}
            </div>

            {/* Quick Create Tenant Modal */}
            {showCreateTenantModal && (
                <div className="modal-overlay" onClick={() => setShowCreateTenantModal(false)}>
                    <div className="edit-minutes-modal" onClick={e => e.stopPropagation()}>
                        <h3>Create New Tenant</h3>
                        <p>Enter a name for the new tenant organization.</p>
                        <div className="modal-input-group">
                            <Input
                                placeholder="Tenant Name"
                                value={quickTenantName}
                                onChange={(e) => setQuickTenantName(e.target.value)}
                                autoFocus
                            />
                        </div>
                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => {
                                setShowCreateTenantModal(false);
                                setQuickTenantName('');
                            }}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => handleCreateTenant(quickTenantName, true)}
                                disabled={!quickTenantName.trim()}
                            >
                                Create Tenant
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Tenant Modal */}
            {editingTenant && (
                <div className="modal-overlay" onClick={() => setEditingTenant(null)}>
                    <div className="edit-tenant-modal" onClick={e => e.stopPropagation()}>
                        <h3>Edit Tenant</h3>
                        <p className="tenant-id-display">ID: <code>{editingTenant.RowKey}</code></p>

                        <div className="form-group">
                            <label htmlFor="edit-tenant-name">Tenant Name</label>
                            <Input
                                id="edit-tenant-name"
                                placeholder="Tenant Name"
                                value={editTenantName}
                                onChange={(e) => setEditTenantName(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-tenant-organization">Organization Name</label>
                            <Input
                                id="edit-tenant-organization"
                                placeholder="Legal organization name (for invoices)"
                                value={editOrganizationName}
                                onChange={(e) => setEditOrganizationName(e.target.value)}
                            />
                        </div>

                        {/* Status and plans are SuperAdmin-only on the server (AdminFunctions.cs). */}
                        {isSuperAdmin && (<>
                        <div className="form-group">
                            <label htmlFor="edit-tenant-status">Account Status</label>
                            <select
                                id="edit-tenant-status"
                                className="ui-select"
                                value={editLifecycleState}
                                onChange={(e) => setEditLifecycleState(e.target.value)}
                            >
                                {LIFECYCLE_STATES.map(state => (
                                    <option key={state.value} value={state.value}>{state.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-tenant-meeting-plan">Meeting Plan</label>
                            <select
                                id="edit-tenant-meeting-plan"
                                className="ui-select"
                                value={editMeetingPlanId}
                                onChange={(e) => setEditMeetingPlanId(e.target.value)}
                            >
                                <option value="">No paid Meeting plan</option>
                                {deduplicatePlans(plans.filter(p => p.active && p.productType !== 'webinar')).map(p => (
                                    <option key={p.id} value={p.id}>
                                        {formatPlanLabel(p)}
                                    </option>
                                ))}
                            </select>
                            {editMeetingPlanId && (
                                <span className="field-hint">
                                    <strong>{formatPlanLabel(plans.find(p => p.id === editMeetingPlanId))}</strong> &middot; {editMeetingPlanId}
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="edit-tenant-webinar-plan">Webinar Plan</label>
                            <select
                                id="edit-tenant-webinar-plan"
                                className="ui-select"
                                value={editWebinarPlanId}
                                onChange={(e) => setEditWebinarPlanId(e.target.value)}
                            >
                                <option value="">No Webinar plan</option>
                                {deduplicatePlans(plans.filter(p => p.active && p.productType === 'webinar')).map(p => (
                                    <option key={p.id} value={p.id}>
                                        {formatPlanLabel(p)}
                                    </option>
                                ))}
                            </select>
                            {editWebinarPlanId && (
                                <span className="field-hint">
                                    <strong>{formatPlanLabel(plans.find(p => p.id === editWebinarPlanId))}</strong> &middot; {editWebinarPlanId}
                                </span>
                            )}
                        </div>
                        </>)}

                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => {
                                setEditingTenant(null);
                                setEditTenantName('');
                                setEditOrganizationName('');
                                setEditLifecycleState('');
                                setEditMeetingPlanId('');
                                setEditWebinarPlanId('');
                            }}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleUpdateTenant}
                                disabled={!editTenantName.trim()}
                            >
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Tenant Confirmation Modal */}
            {deletingTenant && (
                <div className="modal-overlay" onClick={() => setDeletingTenant(null)}>
                    <div className="edit-minutes-modal delete-confirmation" onClick={e => e.stopPropagation()}>
                        <h3>Delete Tenant</h3>
                        <p>Are you sure you want to delete <strong>{deletingTenant.TenantName}</strong>?</p>
                        <p className="warning-text">This action cannot be undone. All associated data will be permanently removed.</p>
                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => setDeletingTenant(null)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="delete-confirm-btn"
                                onClick={handleDeleteTenant}
                            >
                                Delete Tenant
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change role / tenant sheet (phone card layout) */}
            {accessEdit && (() => {
                const { user: target, field, value } = accessEdit;
                const isRole = field === 'role';
                const tenantName = (id) => tenants.find(t => t.RowKey === id)?.TenantName || id || 'No Tenant';
                const unchanged = value === (isRole ? (target.Role || '') : (target.TenantId || ''));
                const landsInBypass = !isRole
                    && value === MASTER_TENANT_ID
                    && MASTER_TENANT_BYPASS_ROLES.includes(target.Role);
                const close = () => {
                    if (isSavingAccess) return;
                    setAccessEdit(null);
                    setAccessError('');
                };
                return (
                    <div className="modal-overlay" onClick={close}>
                        <div
                            className="edit-minutes-modal access-edit-modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="access-edit-title"
                            onClick={e => e.stopPropagation()}
                            onKeyDown={e => { if (e.key === 'Escape') close(); }}
                        >
                            <h3 id="access-edit-title">{isRole ? 'Change role' : 'Change tenant'}</h3>
                            <p className="access-edit-subject">{target.Name && <>{target.Name}<br /></>}{target.Email}</p>

                            <div className="form-group">
                                <label htmlFor="access-edit-select">{isRole ? 'Role' : 'Tenant'}</label>
                                <select
                                    id="access-edit-select"
                                    className="ui-select"
                                    autoFocus
                                    value={value}
                                    onChange={(e) => setAccessEdit({ ...accessEdit, value: e.target.value })}
                                >
                                    {isRole && target.Role
                                        && !getAssignableRoles(user, target).some(role => role.value === target.Role) && (
                                        // Keeps the select showing the role in force rather than the first option.
                                        <option value={target.Role} disabled>{roleLabel(target.Role)} (current)</option>
                                    )}
                                    {isRole
                                        ? getAssignableRoles(user, target).map(role => (
                                            <option key={role.value} value={role.value}>{role.label}</option>
                                        ))
                                        // No "No Tenant": UpdateUser reads an empty tenantId as "no change".
                                        : tenants.map(t => (
                                            <option key={t.RowKey} value={t.RowKey}>{t.TenantName}</option>
                                        ))}
                                </select>
                            </div>

                            {isRole && getRoleChangeBlockedReason(user, target) && (
                                <p className="access-edit-note">{getRoleChangeBlockedReason(user, target)}</p>
                            )}

                            {!isRole && !unchanged && (
                                <p className="access-edit-warning">
                                    Moves this user from {tenantName(target.TenantId)} to {tenantName(value)}.
                                    Their role ({target.Role || 'none'}) and sign-in move with them.
                                    {landsInBypass && ` As ${target.Role} of the master tenant they will be Unlimited (no billing).`}
                                </p>
                            )}

                            {accessError && (
                                <p className="access-edit-error" role="alert">{accessError}</p>
                            )}

                            <div className="modal-actions">
                                <Button variant="ghost" onClick={close} disabled={isSavingAccess}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSaveAccessEdit}
                                    disabled={unchanged || isSavingAccess}
                                >
                                    {isSavingAccess && <Loader2 size={14} className="animate-spin" />}
                                    {isRole ? 'Save role' : 'Move user'}
                                </Button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Delete User Confirmation Modal */}
            {deletingUser && (
                <div className="modal-overlay" onClick={() => { setDeletingUser(null); setDeleteType('soft'); }}>
                    <div className="edit-minutes-modal delete-confirmation" onClick={e => e.stopPropagation()}>
                        <h3>Delete User</h3>
                        <p>Are you sure you want to delete <strong>{deletingUser.Email}</strong>?</p>

                        <div className="delete-type-options">
                            <label className={`delete-option ${deleteType === 'soft' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="deleteType"
                                    value="soft"
                                    checked={deleteType === 'soft'}
                                    onChange={(e) => setDeleteType(e.target.value)}
                                />
                                <div className="option-content">
                                    <span className="option-title">Soft Delete</span>
                                    <span className="option-description">User will be marked as deleted and cannot log in. Data retained for audit purposes.</span>
                                </div>
                            </label>
                            <label className={`delete-option ${deleteType === 'permanent' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="deleteType"
                                    value="permanent"
                                    checked={deleteType === 'permanent'}
                                    onChange={(e) => setDeleteType(e.target.value)}
                                />
                                <div className="option-content">
                                    <span className="option-title">Permanent Delete</span>
                                    <span className="option-description warning">User data will be permanently removed. This action cannot be undone.</span>
                                </div>
                            </label>
                        </div>

                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => { setDeletingUser(null); setDeleteType('soft'); }}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className={`delete-confirm-btn ${deleteType === 'permanent' ? 'permanent' : ''}`}
                                onClick={handleDeleteUser}
                            >
                                {deleteType === 'permanent' ? 'Permanently Delete' : 'Soft Delete'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create User Modal */}
            {showCreateUserModal && (
                <div className="modal-overlay" onClick={() => { setShowCreateUserModal(false); resetCreateUserForm(); }}>
                    <div className="create-user-modal" onClick={e => e.stopPropagation()}>
                        <div className="create-user-header">
                            <UserPlus size={20} className="create-user-icon" />
                            <h3>Create New User</h3>
                        </div>
                        <p className="create-user-subtitle">Register a new user with email and password credentials.</p>

                        <div className="form-group">
                            <label htmlFor="create-user-email">Email Address</label>
                            <Input
                                id="create-user-email"
                                type="email"
                                placeholder="user@example.com"
                                value={createUserEmail}
                                onChange={(e) => setCreateUserEmail(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="create-user-name">Display Name</label>
                            <Input
                                id="create-user-name"
                                type="text"
                                placeholder="John Doe"
                                value={createUserName}
                                onChange={(e) => setCreateUserName(e.target.value)}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="create-user-password">Password</label>
                            <div className="password-input-wrap">
                                <Input
                                    id="create-user-password"
                                    type={createUserShowPassword ? 'text' : 'password'}
                                    placeholder="Minimum 8 characters"
                                    value={createUserPassword}
                                    onChange={(e) => setCreateUserPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setCreateUserShowPassword(!createUserShowPassword)}
                                    aria-label={createUserShowPassword ? 'Hide password' : 'Show password'}
                                >
                                    {createUserShowPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            {createUserPassword && !isPasswordValid(createUserPassword) && (
                                <span className="field-error">
                                    Must be 8+ characters with uppercase, lowercase, and a number
                                </span>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="create-user-tenant">Assign to Tenant</label>
                            <select
                                id="create-user-tenant"
                                className="ui-select"
                                value={createUserTenantId}
                                onChange={(e) => setCreateUserTenantId(e.target.value)}
                            >
                                <option value="">Auto-create new tenant</option>
                                {tenants.map(t => (
                                    <option key={t.RowKey} value={t.RowKey}>{t.TenantName}</option>
                                ))}
                            </select>
                            <span className="field-hint">
                                {createUserTenantId
                                    ? `User will be added to "${tenants.find(t => t.RowKey === createUserTenantId)?.TenantName}"`
                                    : 'A new tenant will be created for this user'}
                            </span>
                        </div>

                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => { setShowCreateUserModal(false); resetCreateUserForm(); }}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleCreateUser}
                                disabled={!createUserEmail.trim() || !isPasswordValid(createUserPassword) || isCreatingUser}
                            >
                                {isCreatingUser ? (
                                    <><Loader2 size={16} className="animate-spin" /> Creating...</>
                                ) : (
                                    'Create User'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Admin Reset Password Modal */}
            {resettingPasswordUser && (
                <div className="modal-overlay" onClick={closeResetPasswordModal}>
                    <div className="create-user-modal" onClick={e => e.stopPropagation()}>
                        <h3>Reset password for {resettingPasswordUser.Name || resettingPasswordUser.Email}</h3>

                        {tempPassword ? (
                            /* Success state — show temporary password */
                            <div>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', fontSize: '14px' }}>
                                    The temporary password has been set. Share it securely with the user.
                                    They should change it immediately after logging in.
                                </p>
                                <div className="admin-temp-password-box">
                                    <span className="admin-temp-password-text">{tempPassword}</span>
                                    <button
                                        type="button"
                                        className="admin-temp-password-copy"
                                        onClick={handleCopyTempPassword}
                                        aria-label="Copy password"
                                    >
                                        {copiedTempPassword ? <CheckCircle size={16} /> : <Copy size={16} />}
                                        {copiedTempPassword ? 'Copied!' : 'Copy'}
                                    </button>
                                </div>
                                <div className="modal-actions" style={{ marginTop: '20px' }}>
                                    <Button variant="primary" onClick={closeResetPasswordModal}>
                                        Done
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            /* Confirmation state */
                            <div>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                                    This will generate a temporary password for <strong>{resettingPasswordUser.Email}</strong>.
                                    The user's current password will be replaced immediately.
                                </p>
                                {resetPasswordError && (
                                    <p style={{ color: 'var(--color-error, #e53e3e)', fontSize: '13px', marginBottom: '12px' }}>
                                        {resetPasswordError}
                                    </p>
                                )}
                                <div className="modal-actions">
                                    <Button variant="ghost" onClick={closeResetPasswordModal} disabled={isResettingPassword}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleAdminResetPassword}
                                        disabled={isResettingPassword}
                                    >
                                        {isResettingPassword ? (
                                            <><Loader2 size={16} className="animate-spin" /> Resetting...</>
                                        ) : (
                                            <><KeyRound size={16} /> Reset Password</>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

export default AdminSettings;
